function computeItemCostUsd(usage, pricing, cacheHitRatio) {
  const inputPricePer1m = pricing.costPer1mCachedInputTokensUsd !== void 0 ? cacheHitRatio * pricing.costPer1mCachedInputTokensUsd + (1 - cacheHitRatio) * pricing.costPer1mInputTokensUsd : pricing.costPer1mInputTokensUsd;
  return (usage.inputTokens * inputPricePer1m + (usage.outputTokens + usage.reasoningTokens) * pricing.costPer1mOutputTokensUsd) / 1e6;
}
function computeUsageCostUsd(usage, pricing) {
  const { audio } = usage;
  if (audio === void 0 || audio.inputTokens === 0 && audio.outputTokens === 0) {
    return computeTextUsageCostUsd(usage, pricing);
  }
  if (pricing.audio === void 0) return null;
  const textUsage = {
    inputTokens: usage.inputTokens - audio.inputTokens,
    cachedInputTokens: usage.cachedInputTokens - audio.cachedInputTokens,
    cacheWriteTokens: usage.cacheWriteTokens,
    cacheWrite12hTokens: usage.cacheWrite12hTokens,
    outputTokens: usage.outputTokens - audio.outputTokens
  };
  return computeTextUsageCostUsd(textUsage, pricing) + computeAudioUsageCostUsd(audio, pricing.audio);
}
function computeAudioUsageCostUsd(audio, pricing) {
  return ((audio.inputTokens - audio.cachedInputTokens) * pricing.costPer1mInputTokensUsd + audio.cachedInputTokens * pricing.costPer1mCachedInputTokensUsd + audio.outputTokens * pricing.costPer1mOutputTokensUsd) / 1e6;
}
function computeTextUsageCostUsd(usage, pricing) {
  const cachedRate = pricing.costPer1mCachedInputTokensUsd ?? pricing.costPer1mInputTokensUsd;
  const writeRate = pricing.costPer1mCacheWriteTokensUsd ?? pricing.costPer1mInputTokensUsd;
  const cachedInputTokens = Math.min(
    usage.cachedInputTokens,
    usage.inputTokens
  );
  const cacheWriteTokens = Math.min(
    usage.cacheWriteTokens,
    usage.inputTokens - cachedInputTokens
  );
  const cacheWrite12hTokens = Math.min(
    usage.cacheWrite12hTokens ?? 0,
    cacheWriteTokens
  );
  const write12hRate = pricing.costPer1mCacheWrite12hTokensUsd ?? writeRate;
  const regularInputTokens = usage.inputTokens - cachedInputTokens - cacheWriteTokens;
  return (regularInputTokens * pricing.costPer1mInputTokensUsd + cachedInputTokens * cachedRate + (cacheWriteTokens - cacheWrite12hTokens) * writeRate + cacheWrite12hTokens * write12hRate + usage.outputTokens * pricing.costPer1mOutputTokensUsd) / 1e6;
}
function computeNormalizedTokenUsageCostUsd(usage, pricing) {
  const cachedInputRate = pricing.costPer1mCachedInputTokensUsd;
  if (usage.inputCacheReadTokens > 0 && cachedInputRate === void 0) {
    return null;
  }
  const cacheWriteRate = pricing.costPer1mCacheWriteTokensUsd;
  if (usage.inputCacheWriteTokens > 0 && cacheWriteRate === void 0) {
    return null;
  }
  const cacheWrite12hRate = pricing.costPer1mCacheWrite12hTokensUsd;
  if (usage.inputCacheWrite12hTokens > 0 && cacheWrite12hRate === void 0) {
    return null;
  }
  const cacheWrite1hRate = pricing.costPer1mCacheWrite1hTokensUsd;
  if (usage.inputCacheWrite1hTokens > 0 && cacheWrite1hRate === void 0) {
    return null;
  }
  const audioInputRate = pricing.audio?.costPer1mInputTokensUsd;
  if (usage.inputAudioTokens > 0 && audioInputRate === void 0) {
    return null;
  }
  return ((usage.inputUncachedTokens + usage.inputToolTokens) * pricing.costPer1mInputTokensUsd + // Undefined is safe only for an empty bucket; nonzero unpriced meters
  // returned null above instead of silently falling back to another rate.
  usage.inputCacheReadTokens * (cachedInputRate ?? 0) + usage.inputCacheWriteTokens * (cacheWriteRate ?? 0) + usage.inputCacheWrite12hTokens * (cacheWrite12hRate ?? 0) + usage.inputCacheWrite1hTokens * (cacheWrite1hRate ?? 0) + usage.inputAudioTokens * (audioInputRate ?? 0) + (usage.outputVisibleTokens + usage.outputReasoningTokens) * pricing.costPer1mOutputTokensUsd) / 1e6;
}
function createModelPricingCatalog(pricingByModel, normalizeModelId = (model) => model) {
  const findPricing = (model) => pricingByModel[normalizeModelId(model)] ?? null;
  return {
    pricedModelIds: Object.freeze(Object.keys(pricingByModel)),
    findPricing,
    computeCostUsd: (model, usage) => {
      const pricing = findPricing(model);
      return pricing === null ? null : computeNormalizedTokenUsageCostUsd(usage, pricing);
    }
  };
}
const APPROXIMATE_CHARS_PER_TOKEN = 4;
const CONSERVATIVE_CHARS_PER_TOKEN = 2.7;
function estimateTokenCountFromChars(charCount, charsPerToken = APPROXIMATE_CHARS_PER_TOKEN) {
  return Math.ceil(charCount / charsPerToken);
}
function estimateTokenCount(text, charsPerToken = APPROXIMATE_CHARS_PER_TOKEN) {
  return estimateTokenCountFromChars(text.length, charsPerToken);
}
const tokenFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0
});
function formatTokenCount(value) {
  return tokenFormatter.format(Math.round(value));
}
function formatUsd(value) {
  const decimals = Math.abs(value) < 1 ? 4 : 2;
  return `$${value.toFixed(decimals)}`;
}
export {
  APPROXIMATE_CHARS_PER_TOKEN,
  CONSERVATIVE_CHARS_PER_TOKEN,
  computeItemCostUsd,
  computeNormalizedTokenUsageCostUsd,
  computeUsageCostUsd,
  createModelPricingCatalog,
  estimateTokenCount,
  estimateTokenCountFromChars,
  formatTokenCount,
  formatUsd
};
//# sourceMappingURL=tokenCost.js.map
